import React, { useState } from 'react';
import styled from 'styled-components';
import { Box, FormControl, RadioGroup, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { boolean, object, string } from 'yup';
import dayjs from 'dayjs';
import { HOD_DOS_DECISION } from './constant';
import { type HodDoSAssessment } from '../../../graphql';
import { PAGE_TEXT } from '../../../lang';
import { type ApplicationRemarkType } from '../../../../Nomination/graphql';
import { checkInvalidDate } from '../../../../Application/graphql';
import {
  Button,
  DatePicker,
  Dialog,
  Radio,
  TextField,
} from '../../../../../components';
import { theme } from '../../../../../Theme';
import { ReturnedRemarksDisplay } from '../../../../Nomination/components';

type HoDDoSAssessmentBoxProps = {
  initialValues: HodDoSAssessment;
  isViewMode: boolean;
  hodRequired: boolean;
  remarks: ApplicationRemarkType[];
  handleSaveAndSubmit: (value: HodDoSAssessment) => Promise<void>;
};

type DesicionOption = {
  value: string;
  label: string;
  attachedField: React.ReactNode;
};
export const HoDDoSAssessmentBox: React.FunctionComponent<
  HoDDoSAssessmentBoxProps
> = props => {
  const {
    initialValues,
    isViewMode,
    hodRequired,
    remarks,
    handleSaveAndSubmit,
  } = props;
  const [dialogOpen, setDialogOpen] = useState(false);

  const STEP_TEXT = PAGE_TEXT.HOD_DOS_ASSESSMENT.ASSESSMENT;
  const isExternalDEARequired = initialValues.externalGrantOrDEA.required;
  const isNonSTEMOrNonSHSRequired = initialValues.nonSTEMOrNonSHS.required;
  const isDualPhDRequired = initialValues.dualPhD.required;
  const isCompetitiveFundRequired = initialValues.competitiveFund.required;
  type RequiredFundingKeys =
    | 'isExternalDEARequired'
    | 'isNonSTEMOrNonSHSRequired'
    | 'isDualPhDRequired'
    | 'isCompetitiveFundRequired';

  const formik = useFormik<HodDoSAssessment>({
    initialValues,
    validationSchema: object({
      decision: string().required('Required').notOneOf([HOD_DOS_DECISION.NA]),
      externalGrantOrDEA: object({
        required: boolean().required(),
        confirmation: boolean().required(),
        accountCode: string().when(
          ['confirmation', 'required'],
          ([confirmation, required], schema) => {
            const accountCodeRequired: boolean =
              confirmation === true && required;
            return accountCodeRequired
              ? schema.required('Required')
              : schema.notRequired();
          },
        ),
      }),
      nonSTEMOrNonSHS: object({
        required: boolean().required(),
        confirmation: boolean().required(),
        assumptionDate: string().when(
          ['confirmation', 'required'],
          ([confirmation, required], schema) => {
            const assumptionDateRequired: boolean =
              confirmation === true && required;
            return assumptionDateRequired
              ? schema.required('Required')
              : schema.notRequired();
          },
        ),
        accountCode: string().when(
          ['confirmation', 'required'],
          ([confirmation, required], schema) => {
            const accountCodeRequired: boolean =
              confirmation === true && required;
            return accountCodeRequired
              ? schema.required('Required')
              : schema.notRequired();
          },
        ),
      }),
      competitiveFund: object({
        required: boolean().required(),
        confirmation: boolean().required(),
        accountCode: string().when(
          ['confirmation', 'required'],
          ([confirmation, required], schema) => {
            const accountCodeRequired: boolean =
              confirmation === true && required;
            return accountCodeRequired
              ? schema.required('Required')
              : schema.notRequired();
          },
        ),
      }),
      dualPhD: object({
        required: boolean().required(),
        confirmation: boolean().required(),
        accountCode: string().when(
          ['confirmation', 'required'],
          ([confirmation, required], schema) => {
            const accountCodeRequired: boolean =
              confirmation === true && required;
            return accountCodeRequired
              ? schema.required('Required')
              : schema.notRequired();
          },
        ),
      }),
      remarks: string().when('decision', ([decision], schema) => {
        const required: boolean = decision === HOD_DOS_DECISION.RETURN_TO_DSRC;
        return required ? schema.required('Required') : schema.notRequired();
      }),
    }),
    onSubmit: async value => {
      // open a dialog to confirm it hod Required
      if (hodRequired) setDialogOpen(true);
      // if hod/dos approval is not required this time
      // then the dialog would not pop up
      // reset and submit directly
      else {
        await handleSaveAndSubmit(value);
      }
    },
  });

  const RequiredContent: Record<
    RequiredFundingKeys,
    {
      title: string;
      fieldName: keyof HodDoSAssessment;
      content: React.ReactNode;
    }
  > = {
    isExternalDEARequired: {
      title: STEP_TEXT.FOR_EXTERNAL.TITLE,
      fieldName: 'externalGrantOrDEA',
      content: (
        <>
          <p>{STEP_TEXT.FOR_EXTERNAL.CONTENT}</p>
          <span className="flex-container mt-4">
            <p className="mt-[1.25rem]">
              {STEP_TEXT.FOR_EXTERNAL.ACCOUNT_CODE}
            </p>
            <TextField
              name="externalGrantOrDEA.accountCode"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              value={formik.values.externalGrantOrDEA.accountCode}
              disabled={isViewMode}
              InputLabelProps={{ shrink: true }}
              error={
                Boolean(formik.touched?.externalGrantOrDEA?.accountCode) &&
                Boolean(formik.errors?.externalGrantOrDEA?.accountCode)
              }
              helperText={
                formik.touched?.externalGrantOrDEA?.accountCode &&
                formik.errors?.externalGrantOrDEA?.accountCode
              }
              sx={{
                margin: '0.25rem 0.5rem',
              }}
            />
          </span>
        </>
      ),
    },
    isNonSTEMOrNonSHSRequired: {
      title: STEP_TEXT.FOR_UNDER_STRATEGIC.TITLE,
      fieldName: 'nonSTEMOrNonSHS',
      content: (
        <>
          <p>{STEP_TEXT.FOR_UNDER_STRATEGIC.CONTENT_1}</p>
          <span className="flex-container mt-4">
            <p className="mt-[1.25rem]">
              {STEP_TEXT.FOR_UNDER_STRATEGIC.CONTENT_2}
            </p>
            <DatePicker
              required
              disabled={isViewMode}
              name="nonSTEMOrNonSHS.assumptionDate"
              onChange={date => {
                void formik.setFieldValue(
                  'nonSTEMOrNonSHS.assumptionDate',
                  date,
                );
              }}
              onBlur={formik.handleBlur}
              type="date"
              InputLabelProps={{ shrink: true }}
              error={
                Boolean(formik.touched?.nonSTEMOrNonSHS?.assumptionDate) &&
                Boolean(formik.errors?.nonSTEMOrNonSHS?.assumptionDate)
              }
              helperText={
                formik.touched?.nonSTEMOrNonSHS?.assumptionDate &&
                formik.errors?.nonSTEMOrNonSHS?.assumptionDate
              }
              value={
                checkInvalidDate(formik.values.nonSTEMOrNonSHS?.assumptionDate)
                  ? null
                  : dayjs(formik.values.nonSTEMOrNonSHS?.assumptionDate)
              }
              sx={{
                margin: '0.25rem 0.5rem',
              }}
            />
          </span>
          <p className="mt-[1.25rem]">{STEP_TEXT.FOR_EXTERNAL.CONTENT}</p>

          <span className="flex-container mt-4">
            <p className="mt-[1.25rem]">
              {STEP_TEXT.FOR_EXTERNAL.ACCOUNT_CODE}
            </p>
            <TextField
              name="nonSTEMOrNonSHS.accountCode"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              value={formik.values.nonSTEMOrNonSHS.accountCode}
              disabled={isViewMode}
              InputLabelProps={{ shrink: true }}
              error={
                Boolean(formik.touched?.nonSTEMOrNonSHS?.accountCode) &&
                Boolean(formik.errors?.nonSTEMOrNonSHS?.accountCode)
              }
              helperText={
                formik.touched?.nonSTEMOrNonSHS?.accountCode &&
                formik.errors?.nonSTEMOrNonSHS?.accountCode
              }
              sx={{
                margin: '0.25rem 0.5rem',
              }}
            />
          </span>
        </>
      ),
    },
    isDualPhDRequired: {
      title: STEP_TEXT.FOR_DUAL.TITLE,
      fieldName: 'dualPhD',
      content: (
        <>
          <p>{STEP_TEXT.FOR_DUAL.CONTENT}</p>
          <span className="flex-container mt-4">
            <p className="mt-[1.25rem]">
              {STEP_TEXT.FOR_EXTERNAL.ACCOUNT_CODE}
            </p>
            <TextField
              name="dualPhD.accountCode"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              value={formik.values.dualPhD.accountCode}
              disabled={isViewMode}
              InputLabelProps={{ shrink: true }}
              error={
                Boolean(formik.touched?.dualPhD?.accountCode) &&
                Boolean(formik.errors?.dualPhD?.accountCode)
              }
              helperText={
                formik.touched?.dualPhD?.accountCode &&
                formik.errors?.dualPhD?.accountCode
              }
              sx={{
                margin: '0.25rem 0.5rem',
              }}
            />
          </span>
        </>
      ),
    },
    isCompetitiveFundRequired: {
      title: STEP_TEXT.FOR_COMPETITIVE.TITLE,
      fieldName: 'competitiveFund',
      content: (
        <>
          <p>{STEP_TEXT.FOR_COMPETITIVE.CONTENT}</p>

          <span className="flex-container mt-4">
            <p className="mt-[1.25rem]">
              {STEP_TEXT.FOR_EXTERNAL.ACCOUNT_CODE}
            </p>
            <TextField
              name="competitiveFund.accountCode"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              value={formik.values.competitiveFund.accountCode}
              disabled={isViewMode}
              InputLabelProps={{ shrink: true }}
              error={
                Boolean(formik.touched?.competitiveFund?.accountCode) &&
                Boolean(formik.errors?.competitiveFund?.accountCode)
              }
              helperText={
                formik.touched?.competitiveFund?.accountCode &&
                formik.errors?.competitiveFund?.accountCode
              }
              sx={{
                margin: '0.25rem 0.5rem',
              }}
            />
          </span>
        </>
      ),
    },
  };

  const getFundingRequiredContent = (): {
    title: React.ReactNode;
    content: React.ReactNode;
    fieldName: keyof HodDoSAssessment;
  } | null => {
    if (isExternalDEARequired) return RequiredContent.isExternalDEARequired;
    if (isNonSTEMOrNonSHSRequired)
      return RequiredContent.isNonSTEMOrNonSHSRequired;
    if (isDualPhDRequired) return RequiredContent.isDualPhDRequired;
    if (isCompetitiveFundRequired)
      return RequiredContent.isCompetitiveFundRequired;
    return null;
  };

  const fundingRequiredContent = getFundingRequiredContent();

  const decisionOption: DesicionOption[] = [
    {
      value: HOD_DOS_DECISION.APPROVE,
      label: STEP_TEXT.APPROVE,
      attachedField: <div>{fundingRequiredContent?.content}</div>,
    },
    {
      value: HOD_DOS_DECISION.RETURN_TO_DSRC,
      label: STEP_TEXT.RETURN_TO_DSRC,
      attachedField: (
        <TextField
          className="w-full"
          multiline
          name="remarks"
          minRows={6}
          label={STEP_TEXT.REMARK_TO_DSRC}
          InputLabelProps={{ shrink: true }}
          value={formik.values.remarks}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isViewMode}
          error={
            Boolean(formik.touched.remarks) && Boolean(formik.errors.remarks)
          }
          helperText={formik.touched.remarks && formik.errors.remarks}
        />
      ),
    },
  ];

  const handleConfirmSaveAndSubmit = async (): Promise<void> => {
    setDialogOpen(false);
    await handleSaveAndSubmit(formik.values);
  };

  const handleChangeDecision = async (
    SyntheticBaseEvent: any,
  ): Promise<void> => {
    const targetDecision = SyntheticBaseEvent.target.value as HOD_DOS_DECISION;
    const requiredFunding: keyof HodDoSAssessment | null =
      fundingRequiredContent?.fieldName ?? null; // to get the field name

    await formik.setFieldValue(
      `decision`,
      targetDecision as HOD_DOS_DECISION.APPROVE,
    );
    await formik.setFieldValue(`remarks`, '');

    if (requiredFunding) {
      await formik.setFieldValue(
        `${requiredFunding}.confirmation`,
        targetDecision === HOD_DOS_DECISION.APPROVE,
      );
      await formik.setFieldValue(`${requiredFunding}.accountCode`, '');

      if (isNonSTEMOrNonSHSRequired) {
        await formik.setFieldValue(`${requiredFunding}.assumptionDate`, '');
      }
    }
  };

  const hasReturnHistory = Boolean(remarks.length);
  return (
    <>
      <Dialog
        openDialog={dialogOpen}
        width={'700px'}
        dialogTitle={PAGE_TEXT.HOD_DOS_ASSESSMENT.SUBMIT_CONFIRM.TITLE}
        dialogAction={PAGE_TEXT.SUBMIT}
        cancelAction={PAGE_TEXT.CANCEL}
        onDialogAction={handleConfirmSaveAndSubmit}
        onDialogClose={() => {
          setDialogOpen(false);
        }}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {PAGE_TEXT.HOD_DOS_ASSESSMENT.SUBMIT_CONFIRM.CONTENT}
        </Typography>
      </Dialog>
      <form onSubmit={formik.handleSubmit} noValidate>
        <ContentBox>
          {hodRequired ? (
            <ContentBox>
              {hasReturnHistory && (
                <Box
                  className="w-full"
                  padding={2}
                  bgcolor={theme.palette.grey['200']}
                >
                  {ReturnedRemarksDisplay(STEP_TEXT.REMARK_TO_DSRC, remarks)}
                </Box>
              )}

              <Box className="w-full" paddingX={4} paddingTop={4}>
                {/* title hint  */}
                <h3 className="font-bold mb-8">
                  {fundingRequiredContent?.title}
                </h3>

                <FormControl className="w-full">
                  <RadioGroup className="!px-8">
                    {decisionOption.map(
                      (item: DesicionOption, index: number) => {
                        const {
                          value: decisionValue,
                          label,
                          attachedField,
                        } = item;
                        const selected =
                          formik.values?.decision === decisionValue;
                        return (
                          <div key={index}>
                            <Radio
                              error={
                                Boolean(formik.touched.decision) &&
                                Boolean(formik.errors.decision)
                              }
                              value={decisionValue}
                              disabled={isViewMode}
                              name="decision"
                              label={label}
                              onChange={handleChangeDecision}
                              checked={selected}
                            />
                            <div className={selected ? 'ml-8 py-4' : 'hidden'}>
                              {attachedField}
                            </div>
                          </div>
                        );
                      },
                    )}
                  </RadioGroup>
                </FormControl>
              </Box>
            </ContentBox>
          ) : (
            <div className="p-4">
              <h2 className="font-semibold">{STEP_TEXT.NOT_REQUIRED_1}</h2>
              {!isViewMode && (
                <h2 className="mt-2 font-bold text-[#951d1d]">
                  {STEP_TEXT.NOT_REQUIRED_2}
                </h2>
              )}
            </div>
          )}
          {!isViewMode && (
            <Box
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              marginY={8}
            >
              <Button
                variant="contained"
                type="submit"
                sx={{ cursor: 'pointer' }}
              >
                {PAGE_TEXT.SUBMIT}
              </Button>
            </Box>
          )}
        </ContentBox>
      </form>
    </>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  flex: 1;

  .flex-container {
    display: flex;
    align-items: flex-start;
  }
`;
